import { useCallback, useEffect, useRef } from "react";

/**
 * WYSIWYG editor for a grammar `form` slide's example conversation. The author types a normal
 * dialogue, selects the words that can change, and clicks "Underline" — those words underline
 * instantly. The author never sees or types a marker.
 *
 * Storage is plain text: each underlined (swappable) word is persisted wrapped in `{{…}}` (the
 * conversation prompt explains that braces = swappable). The braces never reach the author's eyes —
 * they are rendered as an underline here and round-tripped transparently.
 */
interface GrammarFocusUnderlineEditorProps {
  value: string;
  onChange: (text: string) => void;
  placeholder?: string | undefined;
  /** Forces a DOM rebuild when the edited slide changes (so switching slides reloads content). */
  resetKey?: string | undefined;
  /** Render-only (no Underline button, not editable) — used to preview a staged normalization draft. */
  readOnly?: boolean | undefined;
}

const UNDERLINE_STYLE = "2px solid #6366f1"; // matches the FillBlanks blank underline

function makeSwapSpan(text: string): HTMLSpanElement {
  const span = document.createElement("span");
  span.setAttribute("data-swap", "1");
  span.textContent = text;
  span.style.borderBottom = UNDERLINE_STYLE;
  span.style.cursor = "pointer";
  span.title = "Click to remove the underline";
  return span;
}

/** Stored string (with `{{…}}` tokens + `\n`) → DOM, built with nodes (no innerHTML injection). */
function buildDom(container: HTMLElement, value: string) {
  container.replaceChildren();
  const lines = value.split("\n");
  lines.forEach((line, i) => {
    if (i > 0) container.appendChild(document.createElement("br"));
    for (const part of line.split(/(\{\{.*?\}\})/g)) {
      if (!part) continue;
      const m = part.match(/^\{\{([\s\S]*)\}\}$/);
      if (m) container.appendChild(makeSwapSpan(m[1] ?? ""));
      else container.appendChild(document.createTextNode(part));
    }
  });
}

/** DOM → stored string: underlined spans become `{{…}}`, `<br>`/block elements become `\n`. */
function serialize(container: HTMLElement): string {
  let out = "";
  const walk = (node: Node) => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        out += child.textContent ?? "";
      } else if (child.nodeName === "BR") {
        out += "\n";
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        if (el.dataset.swap === "1") {
          // Keep whitespace OUTSIDE the token so underlining " dog " doesn't delete the spaces and
          // glue neighbouring words together. An all-whitespace span degrades to plain text.
          const raw = el.textContent ?? "";
          const m = raw.match(/^(\s*)([\s\S]*?)(\s*)$/);
          const core = m?.[2] ?? "";
          out += core ? `${m?.[1] ?? ""}{{${core}}}${m?.[3] ?? ""}` : raw;
        } else {
          const isBlock = el.nodeName === "DIV" || el.nodeName === "P";
          if (isBlock && out && !out.endsWith("\n")) out += "\n";
          walk(el);
          if (isBlock && !out.endsWith("\n")) out += "\n";
        }
      }
    }
  };
  walk(container);
  return out.replace(/\n+$/, "");
}

export function GrammarFocusUnderlineEditor({
  value,
  onChange,
  placeholder,
  resetKey,
  readOnly = false,
}: GrammarFocusUnderlineEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  // What we last emitted, so an external value/round-trip doesn't rebuild the DOM mid-edit
  // (which would reset the caret).
  const lastEmittedRef = useRef<string | null>(null);
  // Tracks the slide identity so a slide switch ALWAYS reloads — even when the new slide's value
  // happens to equal what we last emitted for the previous slide.
  const lastResetKeyRef = useRef<string | undefined>(undefined);

  // Rebuild the DOM from `value` on initial load and slide switch (resetKey change), and on an
  // external value change — but NOT in response to our own onChange (which would reset the caret).
  useEffect(() => {
    if (!editorRef.current) return;
    const slideChanged = lastResetKeyRef.current !== resetKey;
    lastResetKeyRef.current = resetKey;
    if (!slideChanged && value === lastEmittedRef.current) return; // our own echo — skip rebuild
    buildDom(editorRef.current, value ?? "");
    lastEmittedRef.current = value ?? "";
  }, [value, resetKey]);

  const emit = useCallback(() => {
    if (!editorRef.current) return;
    const text = serialize(editorRef.current);
    lastEmittedRef.current = text;
    onChange(text);
  }, [onChange]);

  const applyUnderline = useCallback(() => {
    const sel = window.getSelection();
    const editor = editorRef.current;
    if (!sel || sel.rangeCount === 0 || !editor) return;
    const range = sel.getRangeAt(0);
    // Ignore an empty or whitespace-only selection — an underlined blank isn't a real swap slot.
    if (
      range.collapsed ||
      !range.toString().trim() ||
      !editor.contains(range.commonAncestorContainer)
    )
      return;
    // Don't underline text that is already inside a swappable span (would nest {{ }} on serialize).
    const inSwap = (node: Node | null) => {
      const el = node instanceof HTMLElement ? node : (node?.parentElement ?? null);
      return !!el?.closest('[data-swap="1"]');
    };
    if (inSwap(range.startContainer) || inSwap(range.endContainer)) return;
    const span = makeSwapSpan("");
    try {
      range.surroundContents(span);
    } catch {
      // surroundContents throws when the selection partially crosses element boundaries;
      // fall back to extracting the contents into the span.
      span.appendChild(range.extractContents());
      range.insertNode(span);
    }
    sel.removeAllRanges();
    emit();
  }, [emit]);

  // Click an underlined word to remove its underline (unwrap the span back to plain text).
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      const target = (e.target as HTMLElement).closest('[data-swap="1"]');
      if (!target || !editorRef.current?.contains(target)) return;
      const parent = target.parentNode;
      if (!parent) return;
      while (target.firstChild) parent.insertBefore(target.firstChild, target);
      parent.removeChild(target);
      emit();
    },
    [emit]
  );

  return (
    <div className="flex flex-col gap-1.5">
      {!readOnly && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={applyUnderline}
            className="px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-100 hover:bg-indigo-200 rounded-lg border border-indigo-200 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            Underline
          </button>
          <span className="text-xs text-blue-700">
            Type an example dialogue. Select a word and press Underline to let the AI replace it
            with other words when it practises with students.
          </span>
        </div>
      )}
      <div
        ref={editorRef}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        role="textbox"
        tabIndex={readOnly ? -1 : 0}
        aria-multiline="true"
        aria-readonly={readOnly}
        aria-label="Grammar example conversation"
        data-placeholder={placeholder}
        onInput={readOnly ? undefined : emit}
        onClick={readOnly ? undefined : handleClick}
        className={`w-full whitespace-pre-wrap break-words border rounded-lg p-2 empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400 ${
          readOnly
            ? "min-h-0 text-gray-700 border-gray-200 bg-gray-50"
            : "min-h-48 text-black border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        }`}
      />
    </div>
  );
}
